/**
 * Safety Banner Component — PRD Section 9.1
 *
 * Non-dismissible, always-present visual element for Brain/Hybrid modes.
 * Renders at standard/elevated/critical levels based on SafetyBannerConfig.
 */

import { useTranslation } from '@tonnie/locale';
import { cn } from '@tonnie/ui';
import type { SafetyBannerConfig } from '@tonnie/copilot';

interface SafetyBannerProps {
  config: SafetyBannerConfig | null;
  className?: string;
}

const LEVEL_STYLES: Record<SafetyBannerConfig['level'], string> = {
  standard: 'bg-muted/50 text-muted-foreground border-muted',
  elevated: 'bg-tone-amber-subtle text-tone-amber border-tone-amber-line',
  critical: 'bg-tone-red-subtle text-tone-red border-tone-red-line',
};

const LEVEL_ICONS: Record<SafetyBannerConfig['level'], string> = {
  standard: 'ℹ',
  elevated: '⚠',
  critical: '⛔',
};

/** The banner carries `ns:key` references (buildSafetyBanner); i18next reads `ns.key`. */
const i18nKey = (ref: string) => ref.replace(':', '.');

export function SafetyBanner({ config, className }: SafetyBannerProps) {
  const { t } = useTranslation();

  if (!config || !config.visible) return null;

  return (
    <div
      className={cn(
        'px-3 py-1.5 text-2xs leading-tight border-b select-none flex items-center gap-1.5',
        LEVEL_STYLES[config.level],
        className
      )}
      role="status"
      aria-live="polite"
    >
      <span aria-hidden="true">{LEVEL_ICONS[config.level]}</span>
      <span className="flex-1">{t(i18nKey(config.message))}</span>
      {config.escalationAction && (
        <a
          href={config.escalationAction.path}
          className="underline hover:no-underline font-medium whitespace-nowrap"
        >
          {t(i18nKey(config.escalationAction.label))}
        </a>
      )}
    </div>
  );
}
